import { useQuery } from 'react-query'
import { Progress } from 'antd'

import { fetchShippingCoverageUpdateStatus } from '../../../services/shippingMethodsServices'

export const POLL_INTERVAL_MS = 2000
// 'unknown' means the backend can no longer see the job: these sweeps commit batch by batch, so
// a worker killed midway really did apply part of its rows. It is terminal — without it the panel
// polls "processing" forever behind a job that no longer exists — but it is not a failure.
const TERMINAL_STATUSES = ['completed', 'partial', 'failed', 'unknown']
export const isTerminal = status => TERMINAL_STATUSES.includes(status)

const CREATE_TASK_TYPE = 'CREATE_SHIPPING_COVERAGES'
const CREATE_COST_TASK_TYPE = 'CREATE_SHIPPING_COSTS'
const UPDATE_COST_TASK_TYPE = 'UPDATE_SHIPPING_COSTS'

/**
 * Shared polling hook for any coverage/cost job (create or bulk update). Both the
 * stand-alone modal and the inline panel consume this so the polling behaviour
 * and terminal logic stay in one place.
 *
 *   jobId         – the jid returned by the 202 response
 *   enabled       – whether to poll (modal: open && jobId; panel: jobId)
 *   onCompleted   – called once on a non-failed terminal status OR on a poll error
 *   statusFetcher – (jid) => Promise of the status body. Defaults to the coverages
 *                   status endpoint; shipping-costs screens pass their own fetcher.
 *   scope         – query-key namespace, so cost and coverage jobs never collide.
 *
 * Returns { data, isError, status, terminal, finalStatus } where finalStatus is
 * the outcome handed back when the user dismisses a finished job (null while
 * still processing, "submitted" if the status endpoint errored).
 */
export const SHIPPING_COSTS_JOB_SCOPE = 'shipping_costs_job_status'

export const useCoverageJobStatus = ({
  jobId,
  enabled,
  onCompleted,
  statusFetcher = fetchShippingCoverageUpdateStatus,
  scope = 'coverage_job_status',
  neutralOnError: neutralOnErrorProp,
}) => {
  // A poll error must NOT be reported as success for a consequential write — a
  // failed/partial job followed by a status-endpoint error would otherwise show a
  // green checkmark. Fall back to a neutral "status unavailable" state and don't
  // fire onCompleted. Cost commits (money) opt in via their scope; any caller can
  // opt in explicitly — "is this write worth being honest about?" is a property of
  // the caller, not of the query key.
  const neutralOnError = neutralOnErrorProp ?? scope === SHIPPING_COSTS_JOB_SCOPE
  const { data, isError } = useQuery([scope, jobId], () => statusFetcher(jobId), {
    enabled: !!enabled && !!jobId,
    // Don't retry a failing status endpoint, and STOP polling once it errors
    // or reaches a terminal state — otherwise the query keeps retrying every
    // few seconds and the UI flips between "processing" and "submitted" forever.
    retry: false,
    refetchInterval: (queryData, query) => {
      if (query?.state?.status === 'error') return false
      if (!queryData) return POLL_INTERVAL_MS
      if (isTerminal(queryData.status)) return false
      return POLL_INTERVAL_MS
    },
    refetchOnWindowFocus: false,
    onSuccess: queryData => {
      if (queryData && isTerminal(queryData.status) && queryData.status !== 'failed') {
        onCompleted?.()
      }
    },
    onError: () => {
      // Coverages: treat as "submitted, status unavailable" and refresh the list so the
      // operator sees any changes that applied. Cost jobs: do NOT report success on a poll
      // error (see neutralOnError) — the commit may have failed.
      if (!neutralOnError) onCompleted?.()
    },
  })

  const status = data?.status
  const terminal = isTerminal(status) || isError
  const finalStatus = isError ? (neutralOnError ? 'unavailable' : 'submitted') : terminal ? status : null

  return { data, isError, status, terminal, finalStatus, neutralOnError }
}

/**
 * Shared visual body for a coverage job: processing / completed / partial /
 * failed / submitted. Pure presentation — no Modal, no polling — so it can be
 * dropped into a stand-alone modal or rendered inline inside another modal.
 */
export const CoverageJobProgressBody = ({ data, isError, neutralOnError = false, t }) => {
  const status = data?.status
  const result = data?.result || {}
  const isCreateTask = data?.task_type === CREATE_TASK_TYPE
  const isCostTask = data?.task_type === CREATE_COST_TASK_TYPE || data?.task_type === UPDATE_COST_TASK_TYPE

  // For cost jobs, a partial/failed commit can differ from the dry-run — surface the
  // exact rows the backend rejected so the operator knows which zones to fix.
  const costRowErrors =
    isCostTask && (result.row_errors || []).length > 0 ? (
      <div className="mx-auto mt-3 max-h-40 max-w-md overflow-y-auto rounded-md border border-gray-200 text-left">
        <table className="min-w-full text-xs">
          <tbody>
            {result.row_errors.map((re, i) => (
              <tr key={i} className="border-b border-gray-100 last:border-0">
                {/* Backend `row` is 0-based; match the grid's 1-based numbering. */}
                <td className="px-2 py-1 align-top font-mono text-gray-500">{typeof re.row === 'number' ? re.row + 1 : (re.id ?? i + 1)}</td>
                <td className="px-2 py-1 text-gray-700">{re.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null

  if (isError) {
    // Cost jobs: a poll error is NOT success — show a neutral "status unavailable" state
    // (no green checkmark) so the operator re-checks whether the change actually applied.
    if (neutralOnError) {
      return (
        <div className="text-center py-6">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
            <span className="text-2xl text-gray-500">?</span>
          </div>
          <div className="mb-1 text-lg font-semibold text-gray-900">{t('coberturas.jobProgress.unavailableTitle')}</div>
          <p className="mx-auto max-w-xs text-sm text-gray-500">{t('coberturas.jobProgress.unavailableBody')}</p>
        </div>
      )
    }
    return (
      <div className="text-center py-6">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <span className="text-2xl text-green-600">✓</span>
        </div>
        <div className="mb-1 text-lg font-semibold text-gray-900">{t('coberturas.jobProgress.submittedTitle')}</div>
        <p className="mx-auto max-w-xs text-sm text-gray-500">{t('coberturas.jobProgress.submittedBody')}</p>
      </div>
    )
  }

  if (!status || status === 'processing') {
    return (
      <div className="py-4">
        <div className="text-base font-semibold text-gray-700 mb-1">{t('coberturas.jobProgress.processing')}</div>
        <p className="text-xs text-gray-400 mb-4">{t('coberturas.jobProgress.processingHint')}</p>
        <Progress percent={data?.progress_percentage ?? 0} status="active" strokeColor="#3b82f6" />
      </div>
    )
  }

  if (status === 'completed') {
    return (
      <div className="text-center py-4">
        <div className="text-2xl mb-2">✓</div>
        <div className="text-lg font-semibold text-green-700 mb-2">{t('coberturas.jobProgress.successTitle')}</div>
        {isCostTask ? (
          <p className="text-sm text-gray-700">
            {t('coberturas.jobProgress.successCosts', {
              succeeded: result.succeeded_count ?? 0,
            })}
          </p>
        ) : isCreateTask ? (
          <p className="text-sm text-gray-700">
            {t('coberturas.jobProgress.successCreate', {
              coverages: result.created_coverages_count ?? 0,
            })}
            {(result.created_shipping_costs_count > 0 || result.created_xdock_zone_bindings_count > 0) &&
              t('coberturas.jobProgress.successCreateExtra', {
                costs: result.created_shipping_costs_count ?? 0,
                zones: result.created_xdock_zone_bindings_count ?? 0,
              })}
          </p>
        ) : (
          <p className="text-sm text-gray-700">{t('coberturas.jobProgress.successUpdate', { rows: result.total_updated_rows ?? 0 })}</p>
        )}
      </div>
    )
  }

  if (status === 'partial') {
    const attempted = result.attempted_count ?? 0
    const created = result.created_coverages_count ?? 0
    return (
      <div className="text-center py-4">
        <div className="text-2xl mb-2">⚠</div>
        <div className="text-lg font-semibold text-yellow-700 mb-2">{t('coberturas.jobProgress.partialTitle')}</div>
        {isCostTask ? (
          <>
            <p className="text-sm text-gray-700">
              {t('coberturas.jobProgress.partialCosts', {
                succeeded: result.succeeded_count ?? 0,
                failed: result.failed_count ?? 0,
                attempted: result.attempted_count ?? 0,
              })}
            </p>
            {costRowErrors}
          </>
        ) : isCreateTask ? (
          <p className="text-sm text-gray-700">
            {t('coberturas.jobProgress.partialCreate', {
              created,
              attempted,
              skipped: attempted - created,
            })}
          </p>
        ) : (
          <p className="text-sm text-gray-700">
            {t('coberturas.jobProgress.partialUpdate', {
              updated: result.success_count ?? 0,
              failed: result.error_count ?? 0,
              rows: result.total_updated_rows ?? 0,
            })}
          </p>
        )}
      </div>
    )
  }

  if (status === 'unknown') {
    return (
      <div className="text-center py-6">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
          <span className="text-2xl text-amber-600">!</span>
        </div>
        <div className="mb-1 text-lg font-semibold text-gray-900">{t('coberturas.jobProgress.interruptedTitle')}</div>
        <p className="mx-auto max-w-xs text-sm text-gray-500">{t('coberturas.jobProgress.interruptedBody')}</p>
      </div>
    )
  }

  if (status === 'failed') {
    return (
      <div className="text-center py-4">
        <div className="text-2xl mb-2">✕</div>
        <div className="text-lg font-semibold text-red-700 mb-2">{t('coberturas.jobProgress.failedTitle')}</div>
        <p className="text-sm text-gray-700 mb-1">{result.error || t('coberturas.jobProgress.failedBody')}</p>
        {isCostTask
          ? costRowErrors
          : result.row_errors && result.row_errors.length > 0 && <p className="text-xs text-gray-500">{t('coberturas.jobProgress.failedRowErrors')}</p>}
      </div>
    )
  }

  return null
}
